// 🔧 promotion_post_types.js
// ✅ Purpose: Central post type registry for the LGV Promotion Engine
// ✅ Features:
// - Defines every supported social post type
// - Associates post types with supported platforms
// - Identifies MVP 1 vs future capabilities
// - Enables queue filtering and rotation tracking
// - Designed so future platforms can be added without changing existing records
// - Fully block-commented for clarity and future devs

import { PROMOTION_PLATFORM_KEYS } from "./promotion_platforms";
import promotionImageSizes from "../../../functions/shared/promotion_image_sizes.json";
import { getPromotionPostCapabilities } from "../../../functions/shared/promotion_post_capabilities.js";

/**
 * Stable post type keys.
 *
 * IMPORTANT:
 * These values become part of Firestore records.
 * Never rename an existing key after release.
 */
export const PROMOTION_POST_TYPE_KEYS = {
  FEED_IMAGE: "feed_image",
  FEED_TEXT: "feed_text",
  FEED_CAROUSEL: "feed_carousel",
  STORY: "story",
  REEL: "reel",
  SHORT_VIDEO: "short_video",
  DOCUMENT: "document",
  POLL: "poll",
  EVENT: "event",
  ARTICLE: "article",
  LINK: "link",
  VIDEO: "video",
  MULTI_IMAGE: "multi_image",
  PHOTO_POST: "photo_post",
  VIDEO_STORY: "video_story",
  CELEBRATION: "celebration",
  RESHARE: "reshare",
};

/**
 * Master list of every post type known by the Promotion Engine.
 *
 * This list is intentionally larger than MVP 1 so that
 * future phases simply enable additional types rather than
 * redesigning the architecture.
 */
export const PROMOTION_POST_TYPES = [
  {
    key: PROMOTION_POST_TYPE_KEYS.FEED_IMAGE,
    label: "Single-image post",
    maxImages: 1,
    enabledForMvp1: true,
    description: "Single image feed post.",
    supportedPlatforms: [
      PROMOTION_PLATFORM_KEYS.LINKEDIN,
      PROMOTION_PLATFORM_KEYS.FACEBOOK,
      PROMOTION_PLATFORM_KEYS.INSTAGRAM,
    ],
  },

  {
    key: PROMOTION_POST_TYPE_KEYS.FEED_TEXT,
    label: "Text post",
    maxImages: 0,
    enabledForMvp1: true,
    description: "Text-only post.",
    supportedPlatforms: [
      PROMOTION_PLATFORM_KEYS.LINKEDIN,
      PROMOTION_PLATFORM_KEYS.FACEBOOK,
    ],
  },

  {
    key: PROMOTION_POST_TYPE_KEYS.FEED_CAROUSEL,
    label: "Carousel (images and videos)",
    maxImages: 10,
    enabledForMvp1: false,
    description: "Multiple image carousel.",
    supportedPlatforms: [
      PROMOTION_PLATFORM_KEYS.INSTAGRAM,
    ],
  },

  {
    key: PROMOTION_POST_TYPE_KEYS.STORY,
    label: "Photo Story",
    maxImages: 1,
    enabledForMvp1: false,
    description: "Temporary story content.",
    supportedPlatforms: [
      PROMOTION_PLATFORM_KEYS.FACEBOOK,
      PROMOTION_PLATFORM_KEYS.INSTAGRAM,
    ],
  },

  {
    key: PROMOTION_POST_TYPE_KEYS.REEL,
    label: "Reel",
    maxImages: 0,
    enabledForMvp1: false,
    description: "Short-form vertical video.",
    supportedPlatforms: [
      PROMOTION_PLATFORM_KEYS.FACEBOOK,
      PROMOTION_PLATFORM_KEYS.INSTAGRAM,
    ],
  },

  {
    key: PROMOTION_POST_TYPE_KEYS.SHORT_VIDEO,
    label: "Video post",
    maxImages: 0,
    enabledForMvp1: false,
    description: "General short video post.",
    supportedPlatforms: [
      PROMOTION_PLATFORM_KEYS.TIKTOK,
    ],
  },

  {
    key: PROMOTION_POST_TYPE_KEYS.DOCUMENT,
    label: "Document",
    maxImages: 0,
    enabledForMvp1: false,
    description: "Document or PDF attachment.",
    supportedPlatforms: [
      PROMOTION_PLATFORM_KEYS.LINKEDIN,
    ],
  },

  {
    key: PROMOTION_POST_TYPE_KEYS.POLL,
    label: "Poll",
    maxImages: 0,
    enabledForMvp1: false,
    description: "Interactive poll.",
    supportedPlatforms: [
      PROMOTION_PLATFORM_KEYS.LINKEDIN,
    ],
  },

  {
    key: PROMOTION_POST_TYPE_KEYS.EVENT,
    label: "Event",
    maxImages: 0,
    enabledForMvp1: false,
    description: "Platform event promotion.",
    supportedPlatforms: [
      // Retained for saved records; event creation is not a normal publishing format.
    ],
  },

  {
    key: PROMOTION_POST_TYPE_KEYS.ARTICLE,
    label: "External article/link preview",
    maxImages: 1,
    enabledForMvp1: false,
    description: "External article/link preview; not a native long-form article.",
    supportedPlatforms: [
      PROMOTION_PLATFORM_KEYS.LINKEDIN,
    ],
  },
  {
    key: PROMOTION_POST_TYPE_KEYS.LINK,
    label: "Link post",
    maxImages: 1,
    enabledForMvp1: false,
    supportedPlatforms: [PROMOTION_PLATFORM_KEYS.FACEBOOK],
  },
  {
    key: PROMOTION_POST_TYPE_KEYS.VIDEO,
    label: "Video post",
    maxImages: 0,
    enabledForMvp1: false,
    supportedPlatforms: [PROMOTION_PLATFORM_KEYS.FACEBOOK, PROMOTION_PLATFORM_KEYS.LINKEDIN],
  },
  {
    key: PROMOTION_POST_TYPE_KEYS.MULTI_IMAGE,
    label: "Multiple-image post",
    // Ten images is the editor cap for Facebook; LinkedIn permits twenty.
    maxImages: 10,
    maxImagesByPlatform: { linkedin: 20 },
    enabledForMvp1: false,
    supportedPlatforms: [PROMOTION_PLATFORM_KEYS.FACEBOOK, PROMOTION_PLATFORM_KEYS.LINKEDIN],
  },
  {
    key: PROMOTION_POST_TYPE_KEYS.PHOTO_POST,
    label: "Photo post (single or multiple)",
    maxImages: 35,
    enabledForMvp1: false,
    supportedPlatforms: [PROMOTION_PLATFORM_KEYS.TIKTOK],
  },
  {
    key: PROMOTION_POST_TYPE_KEYS.VIDEO_STORY,
    label: "Video Story",
    maxImages: 0,
    enabledForMvp1: false,
    supportedPlatforms: [PROMOTION_PLATFORM_KEYS.FACEBOOK, PROMOTION_PLATFORM_KEYS.INSTAGRAM],
  },
  {
    key: PROMOTION_POST_TYPE_KEYS.CELEBRATION,
    label: "Celebration post",
    maxImages: 0,
    enabledForMvp1: false,
    supportedPlatforms: [PROMOTION_PLATFORM_KEYS.LINKEDIN],
  },
  {
    key: PROMOTION_POST_TYPE_KEYS.RESHARE,
    label: "Reshare",
    maxImages: 0,
    enabledForMvp1: false,
    supportedPlatforms: [PROMOTION_PLATFORM_KEYS.LINKEDIN],
  },
];

/**
 * MVP 1 supported post types.
 */
export const PROMOTION_MVP1_POST_TYPES =
  PROMOTION_POST_TYPES.filter(
    (type) => type.enabledForMvp1
  );

/**
 * Dropdown options for UI.
 */
export const PROMOTION_POST_TYPE_OPTIONS =
  PROMOTION_POST_TYPES.map((type) => ({
    value: type.key,
    label: type.label,
  }));

/**
 * MVP 1 dropdown options.
 */
export const PROMOTION_MVP1_POST_TYPE_OPTIONS =
  PROMOTION_MVP1_POST_TYPES.map((type) => ({
    value: type.key,
    label: type.label,
  }));

/**
 * Returns a post type definition.
 */
export const getPromotionPostTypeByKey = (key) => {
  const variant = Object.keys(promotionImageSizes)
    .flatMap(getPromotionPostTypeOptionsForPlatform)
    .find((type) => type.key === key);
  if (variant) return variant;
  return (
    PROMOTION_POST_TYPES.find((type) => type.key === key) ||
    null
  );
};

/**
 * Returns a display label.
 */
export const getPromotionPostTypeLabel = (key) => {
  const type = getPromotionPostTypeByKey(key);

  return type?.label || "Unknown Post Type";
};

/**
 * Returns all supported post types
 * for a particular social platform.
 */
export const getSupportedPostTypesForPlatform = (
  platformKey
) => {
  return PROMOTION_POST_TYPES.filter((type) =>
    type.supportedPlatforms.includes(platformKey)
  ).map((type) => ({ ...type, maxImages: type.maxImagesByPlatform?.[platformKey] ?? type.maxImages }));
};

/** Expand the shared allowlist into selectable size variants without changing saved base keys. */
export const getPromotionPostTypeOptionsForPlatform = (platformKey) => {
  return getSupportedPostTypesForPlatform(platformKey).filter((type) => getPromotionPostCapabilities(platformKey, type.key).supported).flatMap((type) => {
    const sizes = promotionImageSizes[platformKey]?.[type.key];
    if (!sizes) return [];
    if (!sizes.length) return [{ ...type, baseType: type.key, width: null, height: null }];
    return sizes.map((size) => ({
      ...type,
      key: `${type.key}__${size.width}x${size.height}`,
      label: `${type.label} — ${size.label} (${size.width} × ${size.height})`,
      baseType: type.key,
      width: size.width,
      height: size.height,
    }));
  });
};

/** Resolve only a real variant for this platform; arbitrary caller dimensions are never accepted. */
export const getPromotionSizedPostType = (platformKey, key) => {
  return getPromotionPostTypeOptionsForPlatform(platformKey).find((type) => type.key === key) || null;
};

/**
 * Returns MVP 1 supported post types
 * for a particular platform.
 */
export const getSupportedMvp1PostTypesForPlatform = (
  platformKey
) => {
  return PROMOTION_MVP1_POST_TYPES.filter((type) =>
    type.supportedPlatforms.includes(platformKey)
  );
};

/** Recommended production dimensions, width × height; not exhaustive API limits. */
export const PROMOTION_POST_SIZE_ROWS = [
  { platform: "Facebook", contentType: "Text posts", dimensions: "No media dimensions" },
  { platform: "Facebook", contentType: "Link posts", dimensions: "Preview image: 1200 × 630" },
  { platform: "Facebook", contentType: "Single-photo and multiple-photo posts", dimensions: "Square: 1080 × 1080; portrait: 1080 × 1350" },
  { platform: "Facebook", contentType: "Videos", dimensions: "Landscape: 1920 × 1080; square: 1080 × 1080" },
  { platform: "Facebook", contentType: "Reels; photo and video Stories", dimensions: "Vertical: 1080 × 1920 (9:16)" },
  { platform: "Instagram", contentType: "Single-image posts", dimensions: "Square: 1080 × 1080; portrait: 1080 × 1350; landscape: 1080 × 566" },
  { platform: "Instagram", contentType: "Carousels containing images, videos, or both", dimensions: "Consistent slide sizes: 1080 × 1080 or 1080 × 1350" },
  { platform: "Instagram", contentType: "Reels; photo and video Stories", dimensions: "Vertical: 1080 × 1920 (9:16)" },
  { platform: "LinkedIn", contentType: "Text posts; polls; celebration posts; reshares", dimensions: "No fixed media dimensions; attached media follows its format" },
  { platform: "LinkedIn", contentType: "Single-image and multiple-image posts", dimensions: "Square: 1200 × 1200; landscape: 1200 × 627" },
  { platform: "LinkedIn", contentType: "Videos", dimensions: "Landscape: 1920 × 1080; square: 1080 × 1080; vertical: 1080 × 1920" },
  { platform: "LinkedIn", contentType: "Documents, including PDFs", dimensions: "Page-based; no fixed pixel dimensions. Use consistent page sizes" },
  { platform: "LinkedIn", contentType: "External article/link previews", dimensions: "Thumbnail: 1200 × 627" },
  { platform: "TikTok", contentType: "Video posts", dimensions: "Vertical: 1080 × 1920 (9:16). API accepts 360–4096 pixels on each side" },
  { platform: "TikTok", contentType: "Single-photo and multiple-photo posts", dimensions: "Square: 1080 × 1080; vertical: 1080 × 1920. API documents a 1080p maximum" },
];
